// T-171 (design D5): what a form definition's page does with an answer — pure, so tools/ds-tests can run it without a DOM.
import type { ToastType } from './toast.ts';

/** One answer the definition handles (FormView.AnswersJson): a JSON code, or (code null) the followed redirect. */
export interface FormAnswerRule {
  code: number | null;
  redirect: string | null;
  flash: 'success' | 'info' | 'warning' | 'danger';
  message: string | null;
}

/** What the page does after a post: show a message (and stay), or go somewhere with a message for the next page. */
export type Outcome =
  | { kind: 'stay'; message: string; type: ToastType }
  | { kind: 'go'; url: string; message: string | null; type: ToastType };

export const FAILED = 'این کار انجام نشد. صفحه را تازه کنید و دوباره امتحان کنید.';

/**
 * The outcome of an answer (pure, tested): a JSON body with `code` (the legacy pages' answer) or `ok` (the platform's), or a
 * redirect the browser followed (`redirected`, `url`).
 */
export function outcome(
  rules: FormAnswerRule[],
  answer: { status: number; redirected: boolean; url: string; body: unknown },
): Outcome {
  const body = answer.body as { code?: number; message?: string; ok?: boolean; redirect?: string } | null;
  if (body && typeof body === 'object' && typeof body.code === 'number') {
    const rule = rules.find(r => r.code === body.code);
    if (rule) {
      const message = rule.message ?? body.message ?? null;
      return rule.redirect
        ? { kind: 'go', url: rule.redirect, message, type: rule.flash }
        : { kind: 'stay', message: message ?? '', type: rule.flash };
    }
    return body.code >= 200 && body.code < 300
      ? { kind: 'stay', message: body.message ?? '', type: 'success' }
      : { kind: 'stay', message: body.message || FAILED, type: 'danger' };
  }
  if (body && typeof body === 'object' && typeof body.ok === 'boolean') {
    if (!body.ok) return { kind: 'stay', message: body.message || FAILED, type: 'danger' };
    return body.redirect
      ? { kind: 'go', url: body.redirect, message: body.message ?? null, type: 'success' }
      : { kind: 'stay', message: body.message ?? '', type: 'success' };
  }
  if (answer.status >= 200 && answer.status < 400 && (answer.redirected || body === null)) {
    const done = rules.find(r => r.code === null);
    return { kind: 'go', url: answer.url, message: done?.message ?? null, type: done?.flash ?? 'success' };
  }
  return { kind: 'stay', message: FAILED, type: 'danger' };
}
